import { CdkDragDrop, CDK_DRAG_CONFIG, moveItemInArray } from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isObservable, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DestroySubscribers, ISubscribers } from '../../../decorators/destroy-subscribers.decorator';

@Component({
  selector: 'gtd-drag-drop-select',
  templateUrl: './drag-drop-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DragDropSelectComponent),
      multi: true,
    },
    { provide: CDK_DRAG_CONFIG, useValue: { zIndex: 10000 } },
  ],
})
@DestroySubscribers()
export class DragDropSelectComponent implements OnInit, ControlValueAccessor {
  constructor(private breakpointObserver: BreakpointObserver) {}
  @Input() public data: IData | Observable<IData>;
  @Input() public label: string;
  @Input() public bindValue: string | number;
  @Input() public bindLabel: string | number = 'label';
  @Input() public required = false;
  @Input() public writeValueFn: (obj: unknown, primaryCheckboxes: unknown, checkboxes: unknown) => unknown = (obj: unknown): unknown => obj;
  @Input() public changeValueFn: (obj: unknown, primaryCheckboxes: unknown, checkboxes: unknown) => unknown = (obj: unknown): unknown =>
    obj;
  public checkboxesConfigValue: ICheckboxesConfig[];
  @Input() public set checkboxesConfig(value: ICheckboxesConfig[]) {
    this.checkboxesConfigValue = value;
    this._checkboxes = value.map(
      () =>
        new Proxy(
          {},
          {
            set: (target, p, value) => {
              if (value) {
                target[p] = value;
              } else {
                delete target[p];
              }
              this.ctr.updateValueAndValidity();
              return true;
            },
          }
        )
    );
  }
  private _checkboxes: ICheckboxes = [];
  public dataValue: IData;
  public type: 'obj' | 'arr' | 'obs';
  public ctr = new UntypedFormControl([]);
  public subscribers: ISubscribers = {};
  public onChange: (value: any) => void = () => {};
  public onTouch = () => {};

  public ngOnInit(): void {
    switch (true) {
      case isObservable(this.data):
        this.type = 'obs';
        this.subscribers.data = (this.data as Observable<IData>).subscribe((data) => {
          this.dataValue = data;
        });
        break;
      case Array.isArray(this.data):
        this.type = 'arr';
        this.dataValue = this.data as any;
        break;
      default:
        this.type = 'obj';
        this.dataValue = this.data as any;
        break;
    }
    this.subscribers.form = this.ctr.valueChanges.pipe(debounceTime(10)).subscribe((value: any[]) => {
      if (!value?.length) {
        this.clear();
      }
      this.onChange(this.changeValueFn.apply(null, [value].concat(this.checkboxes)));
      this.onTouch();
    });
  }

  public get checkboxes(): ICheckboxes {
    return this._checkboxes;
  }

  public drop(event: CdkDragDrop<string[]>): void {
    const arr = this.ctr.value;
    moveItemInArray(arr, event.previousIndex, event.currentIndex);
    this.ctr.patchValue(arr);
  }

  public swap(a: number, b: number): void {
    const arr = this.ctr.value;
    moveItemInArray(arr, a, b);
    this.ctr.patchValue(arr);
  }

  public get dragDisabled(): boolean {
    return this.breakpointObserver.isMatched(['(max-width: 600px)']);
  }

  public writeValue(value: any): void {
    this.ctr.patchValue(this.writeValueFn.apply(null, [value].concat(this.checkboxes)));
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public addFn = (value: any): void => {
    this.checkboxesConfigValue?.forEach((config, i) => {
      if (config.initialValue) {
        this.checkboxes[i][value] = config.initialValue;
      }
    });
  };

  public removeFn = (value: any): void => {
    for (const obj of this.checkboxes) {
      delete obj[value];
    }
  };

  private clear(): void {
    for (const obj of this.checkboxes) {
      for (const key of Object.keys(obj)) {
        delete obj[key];
      }
    }
  }
}

type IData = Record<string, any>[] | Record<string, string | number>;
type ICheckboxes = Record<string, boolean>[];
interface ICheckboxesConfig {
  key: string;
  label: string;
  initialValue?: true;
}
